<template>
    <div class="mb-5">
        <h2 class="text-xl lg:text-2xl text-gray-800 dark:text-slate-100 font-bold mb-5">
            Travel Goals
        </h2>
        <p class="text-sm text-gray-500 dark:text-slate-400 mb-8">
            Set your travel goals and keep it up to date to get personalized
            flight deals and rewards to your dream destinations.
        </p>

        <form @submit.prevent="submitForm" class="space-y-8">
            <!-- General Error Display -->
            <div
                v-if="form.errors.general"
                class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6"
            >
                {{ form.errors.general }}
            </div>

            <!-- Home Airports Section -->
            <div class="space-y-4">
                <div>
                    <label
                        class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                    >
                        My home airports
                        <span class="text-slate-500 dark:text-slate-400 text-sm font-normal">
                            (optional)
                        </span>
                    </label>

                    <!-- Existing airports -->
                    <div class="space-y-3 mb-4">
                        <div
                            v-for="(airportCode, index) in form.home_airports"
                            :key="index"
                            class="relative"
                        >
                            <AirportSearch
                                label=""
                                placeholder="e.g., Piedmont Triad (GSO), Greensboro, United States"
                                :currentAirport="airportCode"
                                @airportChanged="
                                    (airport) =>
                                        handleAirportChange(airport, index)
                                "
                                @airportCleared="() => removeAirport(index)"
                            />

                            <div
                                v-if="form.errors[`home_airports.${index}`]"
                                class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                                role="alert"
                            >
                                <Icon icon="caution" />
                                {{ form.errors[`home_airports.${index}`] }}
                            </div>
                        </div>
                    </div>

                    <!-- Add airport button -->
                    <button
                        @click="addAirport"
                        type="button"
                        class="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors font-medium"
                    >
                        <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 4v16m8-8H4"
                            ></path>
                        </svg>
                        Add airport
                    </button>

                    <!-- General airports error -->
                    <div
                        v-if="form.errors.home_airports"
                        class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                        role="alert"
                    >
                        <Icon icon="caution" />
                        {{ form.errors.home_airports }}
                    </div>
                </div>
            </div>

            <!-- Dream Destinations Section -->
            <div class="space-y-4">
                <div>
                    <label
                        class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                    >
                        My dream destinations, up to 3
                        <span class="text-slate-500 dark:text-slate-400 text-sm font-normal">
                            (optional)
                        </span>
                    </label>

                    <!-- Existing destinations -->
                    <div class="space-y-3 mb-4">
                        <div
                            v-for="(destination, index) in form.destinations"
                            :key="index"
                            class="relative"
                        >
                            <Input
                                id="trip-destination"
                                name="country"
                                label=""
                                placeholder="e.g., Hawaii, United States"
                                class="mb-4"
                            >
                                <template #input>
                                    <div class="flex items-center gap-2">
                                        <div class="relative grow">
                                            <Suspense>
                                                <PlacesAutocomplete
                                                    :types="['(regions)']"
                                                    id="trip-destination"
                                                    placeholder="e.g., Hawaii, United States"
                                                    :hideClear="false"
                                                    @placeCleared="
                                                        removeDestination(index)
                                                    "
                                                    @placeChanged="
                                                        (data) => {
                                                            handleDestinationChange(
                                                                data,
                                                                index
                                                            );
                                                        }
                                                    "
                                                    :currentPlace="{
                                                        formattedAddress:
                                                            form.destinations[
                                                                index
                                                            ],
                                                    }"
                                                />
                                            </Suspense>
                                        </div>
                                    </div>
                                </template>
                            </Input>

                            <div
                                v-if="form.errors[`destinations.${index}`]"
                                class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                                role="alert"
                            >
                                <Icon icon="caution" />
                                {{ form.errors[`destinations.${index}`] }}
                            </div>
                        </div>
                    </div>

                    <!-- Add destination button -->
                    <button
                        v-if="form.destinations.length < 3"
                        @click="addDestination"
                        type="button"
                        class="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors font-medium"
                    >
                        <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 4v16m8-8H4"
                            ></path>
                        </svg>
                        Add destination
                    </button>

                    <!-- General destinations error -->
                    <div
                        v-if="form.errors.destinations"
                        class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                        role="alert"
                    >
                        <Icon icon="caution" />
                        {{ form.errors.destinations }}
                    </div>
                </div>
            </div>

            <!-- Form Actions -->
            <div
                class="flex items-center justify-end gap-4 pt-6 border-t border-gray-200 dark:border-slate-700"
            >
                <button
                    type="submit"
                    :disabled="form.processing"
                    class="px-4 py-2 text-sm font-medium text-white bg-slate-800 dark:bg-slate-700 border border-transparent rounded-md shadow-sm hover:bg-slate-700 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    :class="{
                        'opacity-50 cursor-not-allowed': form.processing,
                    }"
                >
                    <span
                        v-if="form.processing"
                        class="inline-flex items-center gap-2"
                    >
                        <svg
                            class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        Saving...
                    </span>
                    <span v-else>Save changes</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3';
import { ref, onMounted } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import AirportSearch from '@/components/trips/redemptions/airport-search.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import PlacesAutocomplete from '@/components/ui/places-autocomplete/places-autocomplete.vue';
import { Apii } from '@/services/api.js';
import { notify } from 'notiwind';
import { handleHttpError } from '@/utils/error-handler.js';

const ApiService = new Apii();

const props = defineProps({
    selectedUser: {
        type: Object,
        default: null,
    },
});

const form = useForm({
    _method: 'put',
    home_airports: [''],
    destinations: [''],
});

const addAirport = () => {
    form.home_airports.push('');
};

const removeAirport = (index) => {
    if (form.home_airports.length > 1) {
        form.home_airports.splice(index, 1);
    } else {
        form.home_airports[index] = '';
    }
};

const handleAirportChange = (airport, index) => {
    if (airport && typeof airport === 'object') {
        form.home_airports[index] = airport.code;
    } else if (airport) {
        form.home_airports[index] = airport;
    } else {
        form.home_airports[index] = '';
    }
};

const addDestination = () => {
    if (form.destinations.length < 3) {
        form.destinations.push('');
    }
};

const removeDestination = (index) => {
    if (form.destinations.length > 0) {
        form.destinations.splice(index, 1);
    }
};

const handleDestinationChange = (destination, index) => {
    form.destinations[index] = destination.formattedAddress;
};

const submitForm = () => {
    // Filter out empty values
    const filteredAirports = form.home_airports.filter(
        (code) => code && code.trim() !== ''
    );

    const filteredDestinations = form.destinations
        .filter((dest) => dest.trim() !== '')
        .map((destination, index) => ({
            destination: destination,
            position: index,
        }));

    const payload = {
        _method: 'put',
        home_airports: filteredAirports,
        destinations: filteredDestinations,
    };

    if (props.selectedUser?.uuid) {
        payload.uuid = props.selectedUser.uuid;
    }

    ApiService.apiCall('/user-settings/travel-goals', 'put', payload)
        .then((response) => {
            notify({
                group: 'success',
                title: 'Success',
                text: 'Travel goals saved successfully',
            });
        })
        .catch((error) => {
            handleHttpError(error);
        });
};

function loadGoals() {
    const params = props.selectedUser?.uuid
        ? `?uuid=${props.selectedUser.uuid}`
        : '';

    ApiService.apiCall(`/user-settings/travel-goals${params}`, 'get')
        .then((response) => {
            // Populate home airports
            if (
                response.data.home_airport_codes &&
                response.data.home_airport_codes.length > 0
            ) {
                form.home_airports = [...response.data.home_airport_codes];
            } else {
                form.home_airports = [''];
            }

            // Populate destinations
            if (
                response.data.destinations &&
                response.data.destinations.length > 0
            ) {
                const destinationNames = response.data.destinations.map(
                    (dest) => dest.destination
                );
                form.destinations = destinationNames;
            } else {
                form.destinations = [''];
            }
        })
        .catch((error) => {
            form.home_airports = [''];
            form.destinations = [''];
        });
}

onMounted(() => {
    loadGoals();
});
</script>
